

import { useState, useRef, useEffect } from "react"
import axios from "axios"
import { motion } from "framer-motion"
import { Card, CardContent } from "../ui/card"
import  Button  from "../ui/button"
import PdfViewer from "../../pages/PdfViewer"
import {
  Sparkles,
  FileText,
  Loader2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Code,
  Lightbulb,
  BookOpen,
  Braces,
  Edit,
} from "lucide-react"
import { replacePythonCodeWithImages, extractPythonCodeBlocks } from "./python-image-generator-slides"
import CompactSlideFrameManager from "./slide-frame-manager" // Import the new compact component

export default function SlidesGenerator({ latexCodeSent, onClose }) {
  const [material, setMaterial] = useState(latexCodeSent)
  const [requirement, setRequirement] = useState(
    "Please format these notes as detailed bullet points with equations and include relevant plots.",
  )
  const [pdfUrl, setPdfUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [loadingImage, setLoadingImage] = useState(false)
  const [error, setError] = useState("")
  const [enhancing, setEnhancing] = useState(false)
  const [processingStage, setProcessingStage] = useState("")
  const [warnings, setWarnings] = useState([])
  const [debugInfo, setDebugInfo] = useState(null)
  const [generatedImages, setGeneratedImages] = useState([])
  const [showDebug, setShowDebug] = useState(false)
  const [pdfScrollPosition, setPdfScrollPosition] = useState(0)
  const [includeImages, setIncludeImages] = useState(false)
  const [includeExamples, setIncludeExamples] = useState(false)
  const [author, setAuthor] = useState("AIAT")
  const [compactLayout, setCompactLayout] = useState(false)
  const [isHigh, setIsHigh] = useState(false)
  const [generatedLatexCode, setGeneratedLatexCode] = useState("") // Store generated LaTeX
  const [showFrameManager, setShowFrameManager] = useState(false) // Control visibility of frame manager

  const textareaRef = useRef(null)
  const pdfContainerRef = useRef(null)
  const API_URL = "https://math-image-transcriber-steveszren.replit.app/api/convert"

  const toggleSwitch = () => {
    setIsHigh(!isHigh)
  }

  // Chain of thought effect texts
  const thinkingTexts = [
    "Analyzing material...",
    "Structuring content...",
    "Formatting equations...",
    "Generating LaTeX code...",
    "Identifying visualization opportunities...",
    "Creating Python plot code...",
    "Rendering matplotlib figures...",
    "Converting plots to base64...",
    "Embedding images in LaTeX...",
    "Compiling document...",
    "Finalizing PDF output...",
  ]

  const insertTextAtCursor = (text) => {
    const textarea = textareaRef.current
    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    const newValue = material.substring(0, start) + text + material.substring(end)
    setMaterial(newValue)

    setTimeout(() => {
      textarea.focus()
      textarea.selectionStart = textarea.selectionEnd = start + text.length
    }, 0)
  }

  const handlePaste = (event) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (const item of items) {
      if (item.type.indexOf("image") === 0) {
        event.preventDefault()

        const file = item.getAsFile()
        const formData = new FormData()
        formData.append("image", file)

        setLoadingImage(true)
        setError(null)

        fetch(API_URL, {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.success && data.result) {
              insertTextAtCursor(data.result)
            } else {
              setError(data.error || "Failed to convert image")
            }
          })
          .catch(() => setError("Error uploading image"))
          .finally(() => setLoadingImage(false))

        break
      }
    }
  }

  async function handleGenerateLatex() {
    setLoading(true)
    setError("")
    setPdfUrl("")
    setWarnings([])
    setDebugInfo(null)
    setGeneratedImages([])
    setProcessingStage("Generating LaTeX and Python code...")

        try {
            // Step 1: Generate LaTeX and Python code from OpenAI
            setProcessingStage("Creating LaTeX document with AI...")
            let enhancedRequirement = requirement

            // Add options to the requirement
            if (!includeImages) {
                enhancedRequirement += " Do not include any images or plots."
            } else {
                enhancedRequirement += " Include Python matplotlib code for relevant visualizations."
            }

            if (includeExamples) {
                enhancedRequirement += " Include practical examples to illustrate concepts."
            }

            if (compactLayout) {
                enhancedRequirement += " Use a compact layout with minimal spacing."
            } else {
                enhancedRequirement += " Use a spacious layout with good readability."
            }
            const openAIResponse = await axios.post("/api/generate-latex-slides", {
                userMaterial: material,
                userRequirement: enhancedRequirement,
                customAuthor: author,
                includeImages,
                includeExamples,
                compactLayout,
                isHigh
            })

            let latexCode = openAIResponse.data.latexCode
            if (!latexCode) {
                throw new Error("No LaTeX code was returned from OpenAI.")
            }

            // Make sure the LaTeX preamble includes the graphicx package
            if (!latexCode.includes("\\usepackage{graphicx}")) {
                latexCode = latexCode.replace("\\documentclass{beamer}", "\\documentclass{beamer}\n\\usepackage{graphicx}")
            }

            // Make sure the LaTeX preamble includes the caption package for \captionof
            if (!latexCode.includes("\\usepackage{caption}")) {
                latexCode = latexCode.replace("\\usepackage{graphicx}", "\\usepackage{graphicx}\n\\usepackage{caption}")
            }

      if (latexCode.includes("\\usepackage{filecontents}")) {
        latexCode = latexCode.replace("\\usepackage{filecontents}", "")
      }

      // Store the generated LaTeX code
      setGeneratedLatexCode(latexCode)

      // Compile the LaTeX to show the PDF first
      await compileLatex(latexCode)

      // After PDF is shown, enable the frame manager
      setShowFrameManager(true)
    } catch (err) {
      setError(err.message || "An error occurred")
      setLoading(false)
      setProcessingStage("")
    }
  }

  // Function to compile LaTeX code to PDF
  const compileLatex = async (latexCode) => {
    setLoading(true)
    setProcessingStage("Compiling LaTeX to PDF...")

    try {
      // Check if there are any Python code blocks
      const pythonBlocks = extractPythonCodeBlocks(latexCode)

      if (pythonBlocks.length > 0) {
        // Process Python code blocks and replace with images
        setProcessingStage(`Processing ${pythonBlocks.length} Python code blocks and generating plots...`)

                try {
                    // Replace Python code blocks with images
                    const latexWithImages = await replacePythonCodeWithImages(latexCode)
                    // Step 3: Compile the LaTeX code with shell escape enabled
                    setProcessingStage("Compiling LaTeX to PDF...")
                    const replitResponse = await fetch("/api/compile", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            latexCode: latexWithImages,
                            shellEscape: true,
                        }),
                    })

                    if (!replitResponse.ok) {
                        const errorText = await replitResponse.text()
                        throw new Error(`LaTeX compilation failed: ${errorText}`)
                    }

                    const pdfBlob = await replitResponse.blob()
                    // Create a local URL for the PDF blob so PdfViewer can render it
                    const blobUrl = URL.createObjectURL(pdfBlob)
                    setPdfUrl(blobUrl)
                } catch (plotError) {
                    console.error("Error processing plots:", plotError)
                    setWarnings([...warnings, `Warning: Some plots could not be generated. ${plotError.message}`])

                    // Continue with compilation without plots
                    setProcessingStage("Compiling LaTeX without plots...")
                    const replitResponse = await fetch("/api/compile", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            latexCode: latexCode,
                            shellEscape: true,
                        }),
                    })

                    if (!replitResponse.ok) {
                        const errorText = await replitResponse.text()
                        throw new Error(`LaTeX compilation failed: ${errorText}`)
                    }

                    const pdfBlob = await replitResponse.blob()
                    const blobUrl = URL.createObjectURL(pdfBlob)
                    setPdfUrl(blobUrl)
                }
            } else {
                // No Python code blocks, just compile the LaTeX
                setProcessingStage("Compiling LaTeX...")
                const replitResponse = await fetch("/api/compile", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        latexCode: latexCode,
                        shellEscape: false,
                    }),
                })

                if (!replitResponse.ok) {
                    const errorText = await replitResponse.text()
                    throw new Error(`LaTeX compilation failed: ${errorText}`)
                }

                const pdfBlob = await replitResponse.blob()
                const blobUrl = URL.createObjectURL(pdfBlob)
                setPdfUrl(blobUrl)
            }
        } catch (err) {
            setError(err.message || "An error occurred")
        } finally {
            setLoading(false)
            setProcessingStage("")
        }
    }

  // Function to handle updates from the frame manager
  const handleFrameManagerUpdate = (updatedLatexCode) => {
    setGeneratedLatexCode(updatedLatexCode)
    compileLatex(updatedLatexCode)
  }

  const handleEnhance = async () => {
    if (!material.trim() || enhancing) return
    setEnhancing(true)

        try {
            const response = await fetch("/api/enhance-material", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: material }),
            })

            if (!response.ok) throw new Error("Failed to enhance material")

            const data = await response.json()
            setMaterial(data.enhancedText)
        } catch (error) {
            console.error("Enhancement failed:", error)
        }

        setEnhancing(false)
    }

    // Chain of Thought Effect Component
    const CoTEffect = ({ texts, minHold = 1000, maxHold = 3000 }) => {
        const [index, setIndex] = useState(0)
        const [subIndex, setSubIndex] = useState(0)
        const [phase, setPhase] = useState("typing") // "typing", "holding", "fading"

        useEffect(() => {
            if (phase === "typing") {
                if (subIndex < texts[index].length) {
                    const timeout = setTimeout(() => {
                        setSubIndex((prev) => prev + 1)
                    }, 20)
                    return () => clearTimeout(timeout)
                } else {
                    setPhase("holding")
                    setTimeout(() => setPhase("fading"), Math.random() * (maxHold - minHold) + minHold)
                }
            } else if (phase === "fading") {
                setTimeout(() => {
                    setSubIndex(0)
                    setPhase("typing")
                    setIndex((prev) => (prev + 1) % texts.length)
                }, 1000) // Small pause before restarting
            }
        }, [subIndex, phase, index, texts, minHold, maxHold])

    return (
      <motion.span
        className="ml-2 text-gray-500"
        initial={{ opacity: 0 }}
        animate={{
          opacity:
            phase === "typing" ? 1 : phase === "holding" ? [1, 0.5, 1, 0.7, 1, 0.5, 1, 0.7, 1, 0.5, 1, 0.7, 1] : 0,
        }}
        transition={{ duration: phase === "holding" ? Math.random() * 50.5 + 0.5 : 0.5 }}
      >
        {texts[index].substring(0, subIndex)}
      </motion.span>
    )
  }

  // Handle PDF scroll
  useEffect(() => {
    let handleScroll // Declare handleScroll outside the if block

    if (pdfContainerRef.current && pdfUrl) {
      handleScroll = () => {
        setPdfScrollPosition(pdfContainerRef.current.scrollTop)
      }

      pdfContainerRef.current.addEventListener("scroll", handleScroll)
      return () => {
        if (pdfContainerRef.current) {
          pdfContainerRef.current.removeEventListener("scroll", handleScroll)
        }
      }
    }

    return () => {
      if (pdfContainerRef.current && handleScroll) {
        pdfContainerRef.current.removeEventListener("scroll", handleScroll)
      }
    }
  }, [pdfUrl])

  return (
    <motion.div
      className="fixed inset-0 overflow-y-auto flex flex-col z-50 bg-black/30 pdf-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="overflow-y-auto absolute top-0 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-[90vw] max-w-[90vw]"
        >
          <Card className="shadow-xl rounded-xl border-0 overflow-hidden bg-white dark:bg-gray-900">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-white" />
                <h2 className="text-xl font-bold text-white">Lecture Slides Generator</h2>
              </div>
              <Button
                onClick={onClose}
                variant="ghost"
                className="h-8 w-8 p-0 rounded-full text-white hover:bg-white/20"
              >
                <span className="sr-only">Close</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </Button>
            </div>

            <CardContent className="p-6 grid gap-6">
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="h-4 w-4 text-blue-600" />
                  <label className="font-medium text-gray-900 dark:text-gray-100">Material Content</label>
                </div>
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    className={`w-full h-64 p-4 border rounded-lg font-mono text-sm resize-none bg-gray-50 dark:bg-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      loadingImage ? "blur-sm" : ""
                    } ${loading || loadingImage || enhancing ? "opacity-70" : ""}`}
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                    onPaste={handlePaste}
                    disabled={loading || loadingImage || enhancing}
                    placeholder="Enter your slides material here. You can also paste images to extract text."
                  />
                  {loadingImage && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 rounded-lg">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        <span className="text-gray-700 dark:text-gray-300 font-medium">Processing image...</span>
                      </div>
                    </div>
                  )}

                                    {/* Hint message at the bottom of the textarea */}
                                    <div className="pointer-events-none absolute bottom-3 left-3 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs px-2 py-1 rounded-md opacity-70">
                                        CTRL + V Paste images to insert text at any place
                                    </div>

                                    <motion.button
                                        className="absolute bottom-3 right-3 flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1.5 text-sm font-medium rounded-full shadow-md hover:shadow-lg transition disabled:opacity-50 disabled:pointer-events-none"
                                        onClick={handleEnhance}
                                        disabled={enhancing || loading || loadingImage || !material.trim()}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        {enhancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                        {enhancing ? "Enhancing..." : "Enhance with AI"}
                                    </motion.button>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Lightbulb className="h-4 w-4 text-blue-600" />
                                    <label className="font-medium text-gray-900 dark:text-gray-100">Formatting Requirements</label>
                                </div>
                                <input
                                    className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    type="text"
                                    value={requirement}
                                    onChange={(e) => setRequirement(e.target.value)}
                                    disabled={loading}
                                    placeholder="E.g., Format as bullet points with equations and include relevant plots"
                                />
                            </div>
                            {/* Add this UI section after the "Formatting Requirements" input field and before the warnings section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <FileText className="h-4 w-4 text-blue-600" />
                                        <label className="font-medium text-gray-900 dark:text-gray-100">Document Options</label>
                                    </div>

                                    <div className="bg-gray-50 h-28 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                        <div className="space-y-3">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="includeImages"
                                                    checked={includeImages}
                                                    onChange={(e) => setIncludeImages(e.target.checked)}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    disabled={loading}
                                                />
                                                <label htmlFor="includeImages" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                    Include images and plots
                                                </label>
                                            </div>

                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="includeExamples"
                                                    checked={includeExamples}
                                                    onChange={(e) => setIncludeExamples(e.target.checked)}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    disabled={loading}
                                                />
                                                <label
                                                    htmlFor="includeExamples"
                                                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                                                >
                                                    Include practical examples
                                                </label>
                                            </div>

                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="compactLayout"
                                                    checked={compactLayout}
                                                    onChange={(e) => setCompactLayout(e.target.checked)}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    disabled={loading}
                                                />
                                                <label htmlFor="compactLayout" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                    Use compact layout
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <BookOpen className="h-4 w-4 text-blue-600" />
                                        <label htmlFor="author" className="font-medium text-gray-900 dark:text-gray-100">
                                            Document Author
                                        </label>
                                    </div>
                                    <input
                                        id="author"
                                        type="text"
                                        value={author}
                                        onChange={(e) => setAuthor(e.target.value)}
                                        className="w-full h-28 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        placeholder="Enter document author"
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            {warnings.length > 0 && (
                                <div className="bg-blue-50 dark:bg-blue-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                                    {warnings.map((warning, index) => (
                                        <div key={index} className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                                            <AlertTriangle className="h-4 w-4 mt-1 flex-shrink-0" />
                                            <p className="text-sm">{warning}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {debugInfo && showDebug && (
                                <motion.div
                                    className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-xs"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                >
                                    <div className="flex justify-between">
                                        <p className="font-medium text-gray-700 dark:text-gray-300">
                                            Python blocks: {debugInfo.pythonBlocksCount}
                                        </p>
                                        <p className="font-medium text-gray-700 dark:text-gray-300">
                                            Image inclusions: {debugInfo.imageFiguresCount}
                                        </p>
                                    </div>

                                    {debugInfo.pythonBlocksCount > 0 && debugInfo.imageFiguresCount > 0 && (
                                        <div className="mt-2 grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="font-medium mb-1 text-gray-700 dark:text-gray-300">Python Blocks:</p>
                                                <div className="max-h-24 overflow-y-auto pl-2 border-l-2 border-gray-300 dark:border-gray-600">
                                                    {debugInfo.blockDetails?.map((block, i) => (
                                                        <div key={i} className="mb-1 text-gray-600 dark:text-gray-400">
                                                            <span className="font-medium">Block {block.index + 1}:</span> {block.title}
                                                            {block.plotType !== "Unknown" && (
                                                                <span className="text-gray-500 dark:text-gray-500"> ({block.plotType})</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <p className="font-medium mb-1 text-gray-700 dark:text-gray-300">Image Inclusions:</p>
                                                <div className="max-h-24 overflow-y-auto pl-2 border-l-2 border-gray-300 dark:border-gray-600">
                                                    {debugInfo.figureDetails?.map((fig, i) => (
                                                        <div key={i} className="mb-1 text-gray-600 dark:text-gray-400">
                                                            <span className="font-medium">Figure {fig.index + 1}:</span>
                                                            <span className="text-gray-500 dark:text-gray-500">({fig.type})</span> {fig.caption}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            )}

              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex gap-2">
                  <Button
                    onClick={handleGenerateLatex}
                    disabled={loading || !material.trim()}
                    className="relative overflow-hidden group flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:pointer-events-none"
                  >
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-500/0 via-white/10 to-blue-500/0 group-hover:via-white/20 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></span>
                    <span className="relative flex items-center justify-center">
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin mr-1" />
                      ) : (
                        <FileText className="h-5 w-5 mr-1" />
                      )}
                      <span>{loading ? "Processing..." : "Generate Slides"}</span>
                    </span>
                  </Button>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-400">Advanced Thinking:</span>
                    <button
                      className={`relative w-14 h-7 flex items-center rounded-full p-1 transition-colors ${
                        isHigh ? "bg-blue-500" : "bg-gray-300"
                      }`}
                      onClick={toggleSwitch}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full shadow-md transform duration-300 ${
                          isHigh ? "translate-x-7" : "translate-x-0"
                        }`}
                      />
                    </button>
                    <span className={`text-sm font-medium ${isHigh ? "text-blue-500" : "text-gray-500"}`}>
                      {isHigh ? "Professional (Longer Time)" : "Off (fast)"}
                    </span>
                  </div>
                  {debugInfo && (
                    <Button
                      onClick={() => setShowDebug(!showDebug)}
                      variant="outline"
                      className="flex items-center gap-1 text-xs"
                    >
                      <Braces className="h-3 w-3" />
                      {showDebug ? "Hide" : "Show"} Debug
                      {showDebug ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </Button>
                  )}
                </div>

                {loading && <CoTEffect texts={thinkingTexts} minHold={10000} maxHold={15000} />}
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                </div>
              )}

              {pdfUrl && (
                <motion.div
                  className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-md"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Generated Slides
                    </h3>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs flex items-center gap-1 text-blue-600"
                        onClick={() => setShowFrameManager(!showFrameManager)}
                      >
                        <Edit className="h-3 w-3" />
                        {showFrameManager ? "Hide Editor" : "Edit Slides"}
                      </Button>
                      <a
                        href={pdfUrl}
                        download="lecture_slides.pdf"
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium flex items-center gap-1"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                        </svg>
                        Download PDF
                      </a>
                    </div>
                  </div>

                  <div
                    ref={pdfContainerRef}
                    className="h-[600px] overflow-y-auto relative pdf-container"
                    style={{
                      scrollBehavior: "smooth",
                    }}
                  >
                    <PdfViewer file={pdfUrl} />

                    {/* Scroll indicator */}
                    <div className="absolute right-2 top-2 bottom-2 w-1.5 rounded-full bg-gray-200 dark:bg-gray-700 opacity-70">
                      <motion.div
                        className="w-1.5 rounded-full bg-blue-500"
                        style={{
                          height: pdfContainerRef.current
                            ? `${(pdfContainerRef.current.scrollTop / (pdfContainerRef.current.scrollHeight - pdfContainerRef.current.clientHeight)) * 100}%`
                            : "0%",
                          top: "0px",
                        }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Compact Slide Frame Manager */}
      {pdfUrl && showFrameManager && (
        <CompactSlideFrameManager
          latexCode={generatedLatexCode}
          onUpdate={handleFrameManagerUpdate}
          onClose={() => setShowFrameManager(false)}
        />
      )}
    </motion.div>
  )
}

